import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, AlertTitle, Box, Button, TextField, Tooltip, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import { toast } from 'react-toastify';
import PhoneInput from 'react-phone-number-input/input';
import { getFullAddressPartsFromPlace } from '../../../util/AddressUtil';
import { validatePhoneNumber } from '../../../util/ValidationUtils';
import {
  constructGalleryImageUrl,
  constructListingImageUrl,
} from '../../../util/ConstructImageUrlUtil';
import { AppContext } from '../../../context/app-context';
import { SubscriptionType } from '../../../components/constants/securspace-constants';
import { hasAdminAccess } from '../../../util/AccountUtils';
import { useSnackbar } from '../../../context/snackbar-context';
import { enrichLocationByLatLng } from '../../../components/location/requests/location-requests';
import { getErrorMessage } from '../../../util/NetworkErrorUtil';
import ContainerStackingLoader from '../../../components/location/ContainerStackingLoader';
import SimpleConfirmationDialog from '../../../components/SimpleConfirmationDialog';
import { LocationWizardData } from '../types';
import { LocationEnrichmentResponse, mapEnrichmentToWizardData } from '../locationEnrichment';
import { StepErrors } from '../stepValidation';
import { AddressConfirmationDialogState } from '../useAddressConfirmationGate';
import AspectRatioImageUpload, { ExistingImage } from '../../../components/imageUpload/AspectRatioImageUpload';
import WizardMapPreview from '../components/WizardMapPreview';
import WizardRequiredNote from '../components/WizardRequiredNote';
import AutofillTextField from '../components/AutofillTextField';
import { useEnrichmentVersion } from '../EnrichmentVersionContext';
import { useFeatureFlags } from '../../../hooks/routing/useFeatureFlags';
import AddressConfirmationDialog from '../../../components/address/AddressConfirmationDialog';

type GooglePlaceArg = Parameters<typeof getFullAddressPartsFromPlace>[0];

/** Max number of images allowed in the gallery (matches the legacy uploader). */
const MAX_GALLERY_FILES = 10;

/**
 * Every location photo is stored at 4:3 (width / height) — the shape phone
 * cameras shoot by default, and the middle of the range the public pages
 * display (5:4 profile gallery up to ~1.8:1 landing carousel).
 */
const LOCATION_PHOTO_ASPECT_RATIO = 4 / 3;
const PHONE_FORMAT_HINT = 'Enter a valid 10-digit phone number';
const PHONE_VALID_HINT = '\u2713 Valid phone number';

/**
 * Adapter that lets `react-phone-number-input` render an MUI TextField as its
 * underlying input, so the phone field matches the rest of the MUI form while
 * keeping the library's live formatting / E.164 value behavior intact.
 */
const PhoneTextFieldAdapter = React.forwardRef<HTMLInputElement, Record<string, unknown>>(
  (props, ref) => <TextField {...props} inputRef={ref} fullWidth />
);
PhoneTextFieldAdapter.displayName = 'PhoneTextFieldAdapter';

interface LocationInformationStepProps {
  data: LocationWizardData;
  updateData: (
    partial: Partial<LocationWizardData> | ((prev: LocationWizardData) => Partial<LocationWizardData>)
  ) => void;
  applyEnrichmentData: (
    partial: Partial<LocationWizardData> | ((prev: LocationWizardData) => Partial<LocationWizardData>)
  ) => void;
  errors?: StepErrors;
  listingFile: File | null;
  setListingFile: (file: File | null) => void;
  galleryFiles: File[];
  setGalleryFiles: (files: File[]) => void;
  /** Re-cropped saved gallery photos, by stored file name. */
  galleryReplacements: Map<string, File>;
  replaceGalleryImage: (fileName: string, file: File | null) => void;
  /** Called after enrichment data has been merged into `data`, so the parent can bump `enrichmentVersion`. */
  onEnrichmentApplied: () => void;
  /** True while the address is being geocoded for manual-entry confirmation. */
  checkingAddress?: boolean;
  /** State for the address confirmation dialog (open, typedAddress, suggestedAddress). */
  addressConfirmationDialog?: AddressConfirmationDialogState;
  /** Called when the user picks 'suggested' or 'typed' in the address confirmation dialog. */
  onAddressConfirm?: (selection: 'suggested' | 'typed') => void;
  /** Called when the user closes (cancels) the address confirmation dialog. */
  onAddressConfirmClose?: () => void;
}

/**
 * Step 1 (Info) of the Create/Edit Location wizard — core listing details:
 * name, address (with live map preview), phone number, listing image and gallery.
 */
const LocationInformationStep = (props: LocationInformationStepProps) => {
  const {
    data, updateData, applyEnrichmentData, errors, listingFile, setListingFile, galleryFiles, setGalleryFiles,
    galleryReplacements, replaceGalleryImage,
    onEnrichmentApplied,
    checkingAddress = false,
    addressConfirmationDialog,
    onAddressConfirm,
    onAddressConfirmClose,
  } = props;
  const fieldErrors = errors || {};

  const { user } = useContext(AppContext);
  const { snackbarShowMessage } = useSnackbar();
  const { locationEnrichment } = useFeatureFlags();
  const enrichmentVersion = useEnrichmentVersion();

  // Autofill state.
  const [confirmAutofillOpen, setConfirmAutofillOpen] = useState<boolean>(false);
  const [enriching, setEnriching] = useState<boolean>(false);
  // Holds the in-flight superagent request so it can be aborted on cancel.
  const enrichRequestRef = useRef<{ abort: () => void } | null>(null);

  // The button is GMS Pro–only and requires a coordinate-backed address, which
  // is only set when the user selects a suggestion from the Google Places dropdown.
  // Admins and super admins bypass the subscription gate so they can autofill
  // on behalf of any account regardless of its subscription tier.
  const isGmsPro = user?.subscriptionType === SubscriptionType.GMS_PRO;
  const hasAdminOverride = hasAdminAccess(user);
  const canUseAutofill = isGmsPro || hasAdminOverride;
  const hasSelectedAddress = data.addressLatitude != null && data.addressLongitude != null;
  const autofillEnabled = canUseAutofill && hasSelectedAddress && !enriching;

  const runEnrichment = async () => {
    if (data.addressLatitude == null || data.addressLongitude == null) {
      return;
    }
    setEnriching(true);
    try {
      const req = enrichLocationByLatLng(data.addressLatitude, data.addressLongitude);
      // Superagent requests are also thenable, so store the raw object for abort.
      enrichRequestRef.current = req as unknown as { abort: () => void };
      const res = await req;
      enrichRequestRef.current = null;
      const response = res.body as LocationEnrichmentResponse;
      applyEnrichmentData((prev) => mapEnrichmentToWizardData(response, prev));
      onEnrichmentApplied();

      const warnings = response.warnings || [];
      if (response.fallback?.used) {
        snackbarShowMessage(
          'Limited imagery available \u2014 some fields could not be inferred.',
          'warning'
        );
      } else if (warnings.length > 0) {
        snackbarShowMessage(warnings.join(' '), 'warning');
      } else {
        snackbarShowMessage('Location details autofilled from imagery analysis.', 'success');
      }
    } catch (err: any) {
      // Superagent sets code='ABORTED' when .abort() is called — swallow silently.
      if (err?.code === 'ABORTED' || err?.message === 'Aborted') {
        return;
      }
      snackbarShowMessage(
        getErrorMessage(err, 'Unable to autofill this location. Please try again.'),
        'error'
      );
    } finally {
      enrichRequestRef.current = null;
      setEnriching(false);
    }
  };

  const cancelEnrichment = useCallback(() => {
    enrichRequestRef.current?.abort();
    enrichRequestRef.current = null;
    setEnriching(false);
  }, []);

  // Abort any in-flight enrichment request when the step unmounts (e.g. the
  // user clicks Back / Close mid-request) so the XHR doesn't resolve against
  // an unmounted component and trigger stale state updates.
  useEffect(() => {
    return () => {
      enrichRequestRef.current?.abort();
      enrichRequestRef.current = null;
    };
  }, []);

  const addressInputRef = useRef<HTMLInputElement>(null);
  // Keep the latest updateData in a ref so the one-time autocomplete listener
  // never closes over a stale callback.
  const updateDataRef = useRef(updateData);
  updateDataRef.current = updateData;

  // The Google Maps script (with the `places` library) may not be ready on a
  // hard refresh / deep-link to this page. Poll until it is, then attach.
  const [mapsReady, setMapsReady] = useState<boolean>(Boolean(window.google?.maps?.places));

  useEffect(() => {
    if (mapsReady) return undefined;
    const intervalId = window.setInterval(() => {
      if (window.google?.maps?.places) {
        setMapsReady(true);
        window.clearInterval(intervalId);
      }
    }, 200);
    return () => window.clearInterval(intervalId);
  }, [mapsReady]);

  // Attach Google Places Autocomplete to the address field (same behavior as the
  // legacy create/edit location flow). Selecting a suggestion fills the structured
  // address parts + lat/lng, which drives the map preview.
  useEffect(() => {
    const google = window.google;
    if (!mapsReady || !addressInputRef.current || !google?.maps?.places) {
      return undefined;
    }

    const autocomplete = new google.maps.places.Autocomplete(addressInputRef.current, {
      fields: ['formatted_address', 'geometry', 'address_components'],
      types: ['address'],
      componentRestrictions: { country: 'us' },
    });

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place?.geometry?.location && place?.address_components?.length) {
        updateDataRef.current({
          locationAddress: place.formatted_address || '',
          addressLatitude: place.geometry.location.lat(),
          addressLongitude: place.geometry.location.lng(),
          addressManuallyConfirmed: false,
          ...getFullAddressPartsFromPlace(place as unknown as GooglePlaceArg),
        });
      } else {
        toast.error('Address not found, please select a valid mailing address from the dropdown.');
      }
    });

    return () => {
      google.maps.event.removeListener(listener);
      google.maps.event.clearInstanceListeners(autocomplete);
    };
  }, [mapsReady]);

  const phoneHint = useMemo(() => {
    if (!data.phoneNumber) return null;
    return validatePhoneNumber(data.phoneNumber, true)
      ? { valid: true, text: PHONE_VALID_HINT }
      : { valid: false, text: PHONE_FORMAT_HINT };
  }, [data.phoneNumber]);

  // Stabilise the single-file array identity so AspectRatioImageUpload's object-URL
  // effect doesn't churn (revoke/recreate) the preview on every parent re-render.
  const listingFiles = useMemo(() => (listingFile ? [listingFile] : []), [listingFile]);

  const existingListingImages: ExistingImage[] = useMemo(
    () =>
      data.listingImageFileName
        ? [
            {
              fileName: data.listingImageFileName,
              url: constructListingImageUrl(data.listingImageFileName),
            },
          ]
        : [],
    [data.listingImageFileName]
  );

  const existingGalleryImages: ExistingImage[] = useMemo(
    () =>
      (data.gallery || [])
        .filter((g) => g?.fileName)
        .map((g) => ({
          fileName: g.fileName,
          url: constructGalleryImageUrl(`${data.id || ''}/${g.fileName}`),
          replacement: galleryReplacements.get(g.fileName),
        })),
    [data.gallery, data.id, galleryReplacements]
  );

  const handleReplaceExistingGalleryImage = useCallback(
    (image: ExistingImage, file: File) => replaceGalleryImage(image.fileName, file),
    [replaceGalleryImage]
  );

  // Removing a persisted image just updates local wizard state — the existing
  // Step-1 save (`saveStepToBackend`) reconciles the gallery/listing image via
  // a wholesale replace, so no separate delete-file call is needed here.
  const handleRemoveExistingListingImage = useCallback(() => {
    updateData({ listingImageFileName: '' });
  }, [updateData]);

  const handleRemoveExistingGalleryImage = useCallback(
    (image: ExistingImage) => {
      updateData((prev) => ({
        gallery: (prev.gallery || []).filter((g) => g.fileName !== image.fileName),
      }));
      if (image.replacement) replaceGalleryImage(image.fileName, null);
    },
    [updateData, replaceGalleryImage]
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Location Information
      </Typography>
      <WizardRequiredNote />

      <AutofillTextField
        autofillId="location-information-name"
        label="Location Name"
        required
        fullWidth
        placeholder="Enter Location Name"
        value={data.locationName}
        onChange={(e) => updateData({ locationName: e.target.value })}
        error={Boolean(fieldErrors.locationName)}
        helperText={fieldErrors.locationName || ' '}
        sx={{ mb: 2 }}
      />

      <TextField
        label="Location Address"
        required
        fullWidth
        inputRef={addressInputRef}
        placeholder="Enter Location Address"
        value={data.locationAddress}
        onChange={(e) =>
          // Manual edits invalidate any previously selected coordinates/address parts until a
          // new suggestion is chosen from the Google Places dropdown.
          updateData({
            locationAddress: e.target.value,
            addressLatitude: null,
            addressLongitude: null,
            addressLine1: null,
            addressLine2: null,
            city: null,
            state: null,
            zip: null,
            addressManuallyConfirmed: false,
          })
        }
        error={Boolean(fieldErrors.locationAddress)}
        helperText={fieldErrors.locationAddress || ' '}
        sx={{ mb: 1 }}
      />

      {canUseAutofill && locationEnrichment && (
        <Box sx={{ mb: 2 }}>
          <Tooltip
            title="Select an address from the dropdown to enable autofill"
            disableHoverListener={hasSelectedAddress}
            disableFocusListener={hasSelectedAddress}
            disableTouchListener={hasSelectedAddress}
          >
            {/* span wrapper keeps the tooltip working while the button is disabled */}
            <span>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AutoAwesomeIcon />}
                disabled={!autofillEnabled}
                onClick={() => setConfirmAutofillOpen(true)}
              >
                Autofill Location
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      <PhoneInput
        key={`location-information-phone-number-${enrichmentVersion}`}
        country="US"
        value={data.phoneNumber}
        onChange={(value) => updateData({ phoneNumber: value || '' })}
        inputComponent={PhoneTextFieldAdapter}
        label="Phone Number"
        required
        placeholder="e.g. +12025550100"
        error={Boolean(fieldErrors.phoneNumber)}
        helperText={
          fieldErrors.phoneNumber || (phoneHint ? phoneHint.text : ' ')
        }
      />

      <Box sx={{ mb: 3 }} />

      <WizardMapPreview lat={data.addressLatitude} lng={data.addressLongitude} />

      {/* Carries the "why bother" message once, prominently, instead of repeating
          it in each upload section's helper text. */}
      <Alert
        severity="info"
        icon={<PhotoCameraRoundedIcon fontSize="inherit" />}
        sx={{ mt: 4, alignItems: 'center' }}
      >
        <AlertTitle sx={{ fontWeight: 600, mb: 0.25 }}>Photos get your listing booked</AlertTitle>
        Listings with clear, up-to-date photos build buyer trust and get booked faster. Add a
        profile photo plus a few shots of your lot, entrance and key features.
      </Alert>

      <Box component="section" sx={{ mt: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Listing Image
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The main photo buyers see in search results.
        </Typography>
        <AspectRatioImageUpload
          multiple={false}
          aspectRatio={LOCATION_PHOTO_ASPECT_RATIO}
          files={listingFiles}
          onFilesChange={(files) => setListingFile(files[0] || null)}
          existingImages={existingListingImages}
          onRemoveExisting={handleRemoveExistingListingImage}
        />
      </Box>

      <Box component="section" sx={{ mt: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Image Gallery
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Clear, high quality photos of your lot, entrance and any key features.
        </Typography>
        <AspectRatioImageUpload
          multiple
          aspectRatio={LOCATION_PHOTO_ASPECT_RATIO}
          maxFiles={MAX_GALLERY_FILES}
          files={galleryFiles}
          onFilesChange={setGalleryFiles}
          existingImages={existingGalleryImages}
          onRemoveExisting={handleRemoveExistingGalleryImage}
          onReplaceExisting={handleReplaceExistingGalleryImage}
        />
      </Box>

      <SimpleConfirmationDialog
        open={confirmAutofillOpen}
        title="Autofill location details?"
        body="Autofilling will replace all fields in the form with details inferred from imagery analysis. Any values you've already entered will be overwritten."
        onClose={() => setConfirmAutofillOpen(false)}
        onConfirm={() => {
          setConfirmAutofillOpen(false);
          void runEnrichment();
        }}
      />

      {enriching && <ContainerStackingLoader onCancel={cancelEnrichment} />}

      {addressConfirmationDialog && onAddressConfirm && onAddressConfirmClose && (
        <AddressConfirmationDialog
          open={addressConfirmationDialog.open}
          typedAddress={addressConfirmationDialog.typedAddress}
          suggestedAddress={addressConfirmationDialog.suggestedAddress}
          googleSuppliedParts={addressConfirmationDialog.googleSuppliedParts}
          onClose={onAddressConfirmClose}
          onConfirm={onAddressConfirm}
        />
      )}

      {checkingAddress && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Checking address…
        </Typography>
      )}
    </Box>
  );
};

export default LocationInformationStep;

